import { useEffect, useState } from "react";
import { Tag, Trash2 } from "lucide-react";
import { api, apiError } from "@/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";

const initialForm = { code: "", kind: "percent", value: 25, event_id: "all", max_redemptions: "" };

export function PromoManager({ events }) {
  const [promos, setPromos] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const response = await api.get("/admin/promos");
      setPromos(response.data);
    } catch (error) {
      toast.error(apiError(error, "Promo codes could not be loaded"));
    }
  }

  useEffect(() => { load(); }, []);

  function eventTitle(eventId) {
    return events.find((event) => event.id === eventId)?.title || "All events";
  }

  async function create(event) {
    event.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/promos", {
        code: form.code,
        kind: form.kind,
        value: Number(form.value),
        event_id: form.event_id === "all" ? null : form.event_id,
        max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : null,
      });
      toast.success(`Promo ${form.code.toUpperCase()} created and live on Stripe`);
      setForm(initialForm);
      await load();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSaving(false);
    }
  }

  async function toggle(promo) {
    try {
      await api.patch(`/admin/promos/${promo.id}`, { is_active: !promo.is_active });
      toast.success(promo.is_active ? "Promo disabled" : "Promo enabled");
      await load();
    } catch (error) {
      toast.error(apiError(error));
    }
  }

  async function remove(promo) {
    if (!window.confirm(`Delete promo ${promo.code}? It will be deactivated on Stripe and removed here.`)) return;
    try {
      await api.delete(`/admin/promos/${promo.id}`);
      toast.success("Promo deleted");
      await load();
    } catch (error) {
      toast.error(apiError(error));
    }
  }

  return (
    <section data-testid="promo-manager-section" className="mt-12 border border-white/10 bg-carbon p-6">
      <h2 className="font-display text-3xl text-white">Promo codes</h2>
      <p className="mt-3 max-w-2xl text-sm leading-7 text-zinc-500">Codes sync to Stripe, so attendees can apply them in the cart or type them directly on the Stripe checkout page. Scope a code to one event to push full-album sales after a meet.</p>

      <form data-testid="promo-create-form" onSubmit={create} className="mt-6 grid gap-4 border border-gold/15 bg-white/[0.02] p-5 md:grid-cols-[1fr_140px_140px_1fr_140px_auto] md:items-center">
        <Input data-testid="promo-code-input" required minLength={3} maxLength={24} placeholder="CODE (e.g. FLABOB25)" value={form.code} onChange={(event) => setForm({ ...form, code: event.target.value.toUpperCase() })} className="border-white/10 bg-white/[0.03] font-mono uppercase tracking-[0.2em]" />
        <Select value={form.kind} onValueChange={(value) => setForm({ ...form, kind: value })}>
          <SelectTrigger data-testid="promo-kind-select" className="border-white/10 bg-white/[0.03]"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="percent">% off</SelectItem><SelectItem value="fixed">$ off</SelectItem></SelectContent>
        </Select>
        <Input data-testid="promo-value-input" required type="number" min="1" max={form.kind === "percent" ? 100 : undefined} step="0.01" value={form.value} onChange={(event) => setForm({ ...form, value: event.target.value })} className="border-white/10 bg-white/[0.03]" />
        <Select value={form.event_id} onValueChange={(value) => setForm({ ...form, event_id: value })}>
          <SelectTrigger data-testid="promo-event-select" className="border-white/10 bg-white/[0.03]"><SelectValue placeholder="Scope" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All events</SelectItem>
            {events.map((eventItem) => <SelectItem key={eventItem.id} value={eventItem.id}>{eventItem.title}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input data-testid="promo-max-input" type="number" min="1" placeholder="Max uses" value={form.max_redemptions} onChange={(event) => setForm({ ...form, max_redemptions: event.target.value })} className="border-white/10 bg-white/[0.03]" />
        <Button data-testid="promo-create-button" disabled={saving} className="rounded-none bg-gold font-mono text-xs uppercase tracking-[0.2em] text-onyx hover:bg-titanium">
          <Tag className="h-4 w-4" /> Create
        </Button>
      </form>

      <div className="mt-6 grid gap-3">
        {promos.length === 0 && <p data-testid="promo-empty-state" className="border border-white/10 p-6 text-sm text-zinc-500">No promo codes yet.</p>}
        {promos.map((promo) => (
          <div key={promo.id} data-testid={`promo-row-${promo.code}`} className={`grid gap-3 border border-white/10 p-4 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-center ${promo.is_active ? "" : "opacity-50"}`}>
            <div>
              <p className="font-mono text-sm uppercase tracking-[0.24em] text-gold">{promo.code}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">{promo.kind === "percent" ? `${promo.value}% off` : `$${promo.value} off`} · {eventTitle(promo.event_id)}</p>
            </div>
            <p className="font-mono text-xs text-zinc-400">{promo.times_redeemed}{promo.max_redemptions ? `/${promo.max_redemptions}` : ""} redeemed</p>
            <span data-testid={`promo-status-${promo.code}`} className={`w-fit border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] ${promo.is_active ? "border-gold/40 text-gold" : "border-white/15 text-zinc-500"}`}>{promo.is_active ? "active" : "disabled"}</span>
            <div className="flex gap-2">
              <button data-testid={`promo-toggle-${promo.code}`} onClick={() => toggle(promo)} className="border border-white/15 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-300 transition-colors hover:border-gold/50 hover:text-gold">
                {promo.is_active ? "Disable" : "Enable"}
              </button>
              <button data-testid={`promo-delete-${promo.code}`} onClick={() => remove(promo)} className="border border-red-500/30 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.16em] text-red-300 transition-colors hover:bg-red-500/10">
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
